import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase.config';
import { Mold } from '../types';

export interface LifeCycleAlert {
  level: 'info' | 'warning' | 'danger';
  message: string;
  percentage: number;
}

export class LifeCycleService {
  
  // 수명율 계산
  calculateLifePercentage(currentShots: number, designLife: number): number {
    if (designLife === 0) return 0;
    return Math.round((currentShots / designLife) * 100);
  }

  // 잔여 쇼트 계산
  calculateRemainingShots(currentShots: number, designLife: number): number {
    return Math.max(0, designLife - currentShots);
  }

  // 수명 상태 평가
  evaluateLifeStatus(currentShots: number, designLife: number): LifeCycleAlert {
    const percentage = this.calculateLifePercentage(currentShots, designLife);
    
    if (percentage >= 90) {
      return {
        level: 'danger',
        message: '금형 교체 필요',
        percentage
      };
    } else if (percentage >= 80) {
      return {
        level: 'warning', 
        message: '금형 점검 필요',
        percentage
      };
    } else if (percentage >= 70) {
      return {
        level: 'warning',
        message: '수명 관리 주의',
        percentage
      };
    } else {
      return {
        level: 'info',
        message: '정상 사용 가능',
        percentage
      };
    }
  }

  // 쇼트 카운팅 업데이트
  async updateShotCount(moldId: string, currentShots: number, additionalShots: number): Promise<void> {
    try {
      const moldRef = doc(db, 'molds', moldId);
      
      // 현재 쇼트에 추가 쇼트 더하기
      await updateDoc(moldRef, {
        currentShots: currentShots + additionalShots,
        updatedAt: Timestamp.now()
      });
      
    } catch (error) {
      console.error('쇼트 카운팅 업데이트 실패:', error);
      throw new Error('쇼트 카운팅 업데이트에 실패했습니다.');
    }
  }

  // 수명 예측 (사용 패턴 기반)
  predictLifeExpiration(mold: Mold, averageDailyShots: number): Date | null {
    const remainingShots = this.calculateRemainingShots(mold.currentShots, mold.designLife);
    
    if (remainingShots <= 0 || averageDailyShots <= 0) {
      return null;
    }
    
    const daysRemaining = Math.ceil(remainingShots / averageDailyShots);
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + daysRemaining);
    
    return expirationDate;
  }

  // 알림 필요 여부 판단
  shouldTriggerAlert(mold: Mold): boolean {
    const status = this.evaluateLifeStatus(mold.currentShots, mold.designLife);
    return status.level === 'warning' || status.level === 'danger';
  }

  // 수명 단계별 메시지
  getLifeStageMessage(percentage: number): string {
    if (percentage >= 95) return '즉시 교체 필요';
    if (percentage >= 90) return '교체 준비 필요';
    if (percentage >= 80) return '점검 스케줄 필요';
    if (percentage >= 70) return '사용량 모니터링 필요';
    if (percentage >= 50) return '중간 점검 권장';
    return '정상 사용 중';
  }

  // 비용 효율성 계산 (쇼트당 비용)
  calculateCostPerShot(moldCost: number, totalShots: number): number {
    if (totalShots === 0) return 0;
    return moldCost / totalShots;
  }
}

export const lifeCycleService = new LifeCycleService();
